import { N_TIMELINE, NOW_IDX, STEP_MIN } from './time';
import type {
  HistoryResponse, PvForecastResponse, SocForecastResponse, PricesResponse,
  BmsConditioningResponse,
} from '../api/types';
import type { TooltipBand } from '../components/charts/tooltip';
import type { StrategyKey } from '../components/charts/strategy';

// Threshold for classifying observed battery flow into a strategy band.
// 500W filters self-discharge drift and measurement noise (~±200W idle).
// Real charge/discharge events run 5-50 kW so the threshold can't miss any.
// Sign convention: battery_w > 0 = charging, < 0 = discharging (Solis register).
const BATTERY_FLOW_THRESHOLD_W = 500;

export interface Timeline {
  pvP: (number | null)[]; pvF: (number | null)[];
  pvW: (number | null)[]; // live weather PV-forecast (separate from planner snapshot pvF)
  baP: (number | null)[]; baF: (number | null)[];
  grP: (number | null)[]; grF: (number | null)[];
  glP: (number | null)[]; glF: (number | null)[];
  bkP: (number | null)[]; bkF: (number | null)[];
  soP: (number | null)[]; soF: (number | null)[];
  evF: (number | null)[]; // EV-laad-forecast (kWh/h ≈ kW), 0 op uren zonder EV-overlay
  // Source: 'hint' (anticipatie), 'detector' (live grond-truth), of 'fusion'
  // (detector kW × hint kWh-volume — beide bronnen leveren). Visueel: hint
  // = lichte cyaan, detector + fusion = donker-cyaan (zelfde kleur want voor
  // de gebruiker is "EV laadt nu" de relevante boodschap, ongeacht of de
  // window-lengte uit hint of detector-history komt).
  evBands: { startIdx: number; endIdx: number; source: 'hint' | 'detector' | 'fusion' }[];
  // EV-arrival marker (alleen gevuld bij user-hint, niet bij detector — een
  // detector ziet de EV al laden, "thuis-uur" is dan irrelevant). De SOC-waarde
  // op dat tijdstip toont visueel hoeveel de planner gereserveerd heeft.
  evArrivalIdx: number | null;
  evArrivalSoc: number | null;
  evArrivalLabel: string | null; // bv. "🔌 EV 18:00 (40% leeg)"
  buy: (number | null)[]; sell: (number | null)[];
  bands: TooltipBand[];
  // Perioden in het verleden waarin de EMS op strategie 'manual' draaide
  // (force-actie of handmatig setpoint), uit history.mode. Getekend als
  // arcering over de strategieband zodat een handmatige ingreep niet meer
  // voor een planner-besluit kan doorgaan.
  manualBands: { startIdx: number; endIdx: number }[];
}

function emptyArr(): (number | null)[] { return new Array(N_TIMELINE + 1).fill(null); }
function tsToIdx(ts: string, now: Date, nowIdx: number = NOW_IDX): number {
  const minutes = (new Date(ts).getTime() - now.getTime()) / 60000;
  return Math.round(nowIdx + minutes / STEP_MIN);
}
function inRange(idx: number): boolean { return idx >= 0 && idx <= N_TIMELINE; }

// battery_change_kwh threshold above which idle is classified as PV-fill charging.
// 0.3 kWh/h ≈ 300W average — filters rounding/standby noise, catches real PV surplus.
const PV_CHARGE_THRESHOLD_KWH = 0.3;

/**
 * Slotlengte van de planner-prognose, in minuten, uit de reeks zelf.
 *
 * Niet uit een aanname en niet uit config: het verschil tussen twee
 * opeenvolgende slotbeginnen ís de slotlengte. Minder dan twee slots, of
 * onleesbare tijden, geven 60 terug — het gedrag van vóór de kwartierresolutie.
 */
export function forecastSlotMinutes(
  trajectory: { hour_start: string }[] | null | undefined,
): number {
  if (!trajectory || trajectory.length < 2) return 60;
  const a = new Date(trajectory[0].hour_start).getTime();
  const b = new Date(trajectory[1].hour_start).getTime();
  const min = Math.round((b - a) / 60000);
  return Number.isFinite(min) && min > 0 ? min : 60;
}

/**
 * Slotlengte in minuten PER RIJ van de trajectorie.
 *
 * `forecastSlotMinutes` leest alleen de eerste twee rijen. Dat gaat mis zodra de
 * horizon GEMENGD is, en dat is precies wat er op Vossenjacht gebeurt zodra de
 * kwartierprijzen voor vandaag gepubliceerd zijn terwijl morgen nog op uurbasis
 * staat. Gemeten 2026-07-31 op VJ: 32 stappen van 15 minuten, daarna 23 van 60,
 * omslag om 22:00.
 *
 * Met één enkele meting werd `trajBuckets` daar 1, waardoor élk uur-slot na de
 * omslag een kwart zo breed werd getekend en `kwPerKwh` er viermaal te hoog
 * uitkwam. Op het scherm: een grafiek die ruim zeventien uur te vroeg ophoudt
 * met een samengeperste staart.
 *
 * De breedte van een rij is de afstand tot de VOLGENDE rij. De laatste rij heeft
 * geen opvolger en erft de vorige breedte; is er maar één rij, dan een uur —
 * het gedrag van vóór de kwartierresolutie.
 */
export function slotMinutesPerEntry(
  trajectory: { hour_start: string }[] | null | undefined,
): number[] {
  if (!trajectory || trajectory.length === 0) return [];
  if (trajectory.length === 1) return [60];
  const uit: number[] = [];
  for (let i = 0; i < trajectory.length - 1; i++) {
    const a = new Date(trajectory[i].hour_start).getTime();
    const b = new Date(trajectory[i + 1].hour_start).getTime();
    const min = Math.round((b - a) / 60000);
    uit.push(Number.isFinite(min) && min > 0 ? min : 60);
  }
  uit.push(uit[uit.length - 1]);
  return uit;
}

// Laad-strategieën die BMS-conditionering kan overnemen. Alleen wat de accu
// laadt: een conditioning-venster dat toevallig ontladen of standby overlapt
// blijft de eigen kleur houden.
const CHARGE_STRATEGIES = new Set<StrategyKey>(['charge', 'pv_fill', 'pv_charge', 'charge_only']);

interface ConditioningWindow { startMs: number; endMs: number }

/**
 * Conditioning-vensters uit /api/bms-conditioning: actieve cyclus
 * [started, deadline ?? expires], history [started, completed ?? deadline].
 * History zonder voltooiing en zonder laden (uitgesteld of geannuleerd) levert
 * geen venster op. Onleesbare tijden of geen respons geven een lege lijst, dus
 * exact het oude gedrag.
 */
export function conditioningWindows(c: BmsConditioningResponse | null | undefined): ConditioningWindow[] {
  const out: ConditioningWindow[] = [];
  const add = (a?: string | null, b?: string | null) => {
    if (!a || !b) return;
    const startMs = new Date(a).getTime(), endMs = new Date(b).getTime();
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) out.push({ startMs, endMs });
  };
  if (!c) return out;
  if (c.active_cycle) add(c.active_cycle.started_at_utc, c.active_cycle.deadline_utc ?? c.active_cycle.expires_at_utc);
  for (const e of c.cycle_history ?? []) {
    if (!e.completed_at_utc && (e.outcome === 'deferred_ev_hint' || e.outcome === 'user_cancelled')) continue;
    add(e.started_at_utc, e.completed_at_utc ?? e.deadline_utc);
  }
  return out;
}

function inWindow(ms: number, windows: ConditioningWindow[]): boolean {
  return windows.some((w) => ms >= w.startMs && ms < w.endMs);
}

function idleStrategy(batteryChangeKwh: number | null | undefined): StrategyKey {
  return (batteryChangeKwh ?? 0) > PV_CHARGE_THRESHOLD_KWH ? 'pv_charge' : 'selfuse';
}

export function assembleTimeline(
  now: Date,
  hist: HistoryResponse | null,
  prices: PricesResponse | null,
  pvForecast: PvForecastResponse | null,
  socForecast: SocForecastResponse | null,
  nowIdx: number = NOW_IDX,
  conditioning: BmsConditioningResponse | null = null,
): Timeline {
  const pvP = emptyArr(), pvF = emptyArr(), pvW = emptyArr();
  const baP = emptyArr(), baF = emptyArr();
  const grP = emptyArr(), grF = emptyArr();
  const glP = emptyArr(), glF = emptyArr();
  const bkP = emptyArr(), bkF = emptyArr();
  const soP = emptyArr(), soF = emptyArr();
  const evF = emptyArr();
  const evBands: { startIdx: number; endIdx: number; source: 'hint' | 'detector' | 'fusion' }[] = [];
  const buy = emptyArr(), sell = emptyArr();

  // History → past arrays. Strategy bands derived from OBSERVED battery flow,
  // not EMS mode/engagement. Mode says intent; observed power says what
  // actually happened. With threshold ±500W to filter idle noise.
  const pastStrategyByIdx: (StrategyKey | null)[] = new Array(N_TIMELINE + 1).fill(null);
  const pastModeByIdx: (string | null)[] = new Array(N_TIMELINE + 1).fill(null);
  const pastEngagementByIdx: (string | null)[] = new Array(N_TIMELINE + 1).fill(null);
  // Laadpoort-weigering per bucket (history.blocked_by/blocked_reason). Zelfde
  // last-wins-per-index als mode/engagement, zodat strategie en weigering bij
  // hetzelfde sample horen.
  const pastBlockedByIdx: (string | null)[] = new Array(N_TIMELINE + 1).fill(null);
  const pastBlockedReasonByIdx: (string | null)[] = new Array(N_TIMELINE + 1).fill(null);
  // Past EV-active flag per bucket — gevuld vanuit history sample.ev_active.
  // Later geconverteerd naar contiguous evBands voor visual parity met future.
  const pastEvActiveByIdx: boolean[] = new Array(N_TIMELINE + 1).fill(false);
  if (hist?.samples) for (const s of hist.samples) {
    const i = tsToIdx(s.ts, now, nowIdx);
    if (!inRange(i) || i > nowIdx) continue;
    pvP[i] = s.pv_w != null ? s.pv_w / 1000 : null;
    baP[i] = s.battery_w != null ? s.battery_w / 1000 : null;
    grP[i] = s.grid_w != null ? s.grid_w / 1000 : null;
    glP[i] = s.grid_load_w != null ? s.grid_load_w / 1000 : null;
    bkP[i] = s.backup_w != null ? s.backup_w / 1000 : null;
    soP[i] = s.soc_pct;
    buy[i] = s.buy_eur_kwh;
    sell[i] = s.sell_eur_kwh;
    pastModeByIdx[i] = s.mode ?? null;
    pastEngagementByIdx[i] = s.engagement ?? null;
    pastBlockedByIdx[i] = s.blocked_by ?? null;
    pastBlockedReasonByIdx[i] = s.blocked_by ? (s.blocked_reason ?? null) : null;
    if (s.ev_active) pastEvActiveByIdx[i] = true;
    // Three-step classification (cycle #29 fix — past/future parity):
    //   1. engagement='yield' + EMS mode='pv_fill' → EMS engageert SC TOU
    //      charge bewust; toon 'pv_fill' band (warmer geel) i.p.v. selfuse.
    //   2. engagement='yield' + batt charging > +500W → 'pv_charge'
    //      (zon-geel). Same logic als future idleStrategy() — voorkomt dat
    //      historische PV-fill ochtenden in oranje "Eigen verbruik" tonen
    //      terwijl morgen's PV-fill in geel "PV-laden" toont. Same physics,
    //      same color.
    //   3. engagement='yield' (rest) → 'selfuse' (battery flat of dischargen
    //      door huishouden — Solis self-use absorbeert).
    //   4. engagement='engage' OR null (legacy) → EMS dispatching:
    //        4a. discharge mapping: battery_w < -500W ALTIJD → 'discharge'
    //            (echte discharges duren 20-60 min, geen false-positive risico).
    //        4b. charge mapping: alleen 'charge' als mode expliciet
    //            'charge' of 'pv_fill' is (= planner-bedoeld charge-uur).
    //            Diederik-NS-feedback 19/5: 5-min battery_w+500W puls tijdens
    //            mode=revenue_max+engage (SC re-engage flip, geen planner-
    //            charge) rendered als phantom 'charge'-band in onze grafiek
    //            terwijl Solis Cloud terecht niets als 'charge' toonde.
    //            Bram-decision optie B: alleen 'charge' tonen bij echte
    //            planner-charge-uur (mode='charge'/'pv_fill'), rest → selfuse.
    // 0. engagement='feedin' → eigen band. Dit MOET vóór alle andere takken
    //    staan. Bij feed-in houdt de EMS de accu vast en gaat het PV-overschot
    //    rechtstreeks naar het net; het setpoint is nul en battery_w ligt rond
    //    nul. Zonder deze tak viel feed-in in de battery_w-classificatie en
    //    kreeg het dezelfde oranje 'selfuse'-band als Solis self-use — terwijl
    //    dat fysiek het tegenovergestelde is (self-use laadt het overschot
    //    juist de accu in). Bram, 2026-07-30: "ik zie er geen aparte kleur voor
    //    terug in de tarief- en strategiegrafiek."
    //
    //    Werkt met terugwerkende kracht zonder migratie: `engagement` staat al
    //    per historisch punt in de geschiedenis, dus oude feed-in-vensters
    //    kleuren vanzelf mee zodra deze regel er staat.
    if (s.engagement === 'feedin') {
      pastStrategyByIdx[i] = 'feedin';
    } else if (s.engagement === 'yield') {
      if (s.mode === 'pv_fill') {
        pastStrategyByIdx[i] = 'pv_fill';
      } else if (s.battery_w != null && s.battery_w > BATTERY_FLOW_THRESHOLD_W) {
        pastStrategyByIdx[i] = 'pv_charge';
      } else {
        pastStrategyByIdx[i] = 'selfuse';
      }
    } else if (s.battery_w != null) {
      if (s.battery_w < -BATTERY_FLOW_THRESHOLD_W) {
        pastStrategyByIdx[i] = 'discharge';
      } else if (s.battery_w > BATTERY_FLOW_THRESHOLD_W) {
        // engage + battery_w > threshold = provisional charge. A duration-aware
        // post-pass (below) demotes ISOLATED single-bucket charges in non-explicit
        // charge-modes (e.g. revenue_max) to 'selfuse': those are SC-re-engage
        // micro-pulses, not planner grid-charge. Real grid-charge windows span
        // many consecutive buckets and stay 'charge'. (Diederik-feedback 19/5 +
        // Bram-decision optie B; history has no planner 'action' field so
        // run-length is the only signal that separates the two.)
        pastStrategyByIdx[i] = 'charge';
      } else {
        pastStrategyByIdx[i] = 'selfuse';
      }
    }
  }

  // Duration-aware phantom-charge filter (Diederik-feedback 19/5, Bram-decision
  // optie B): a real grid-charge window spans many consecutive 5-min buckets,
  // while an SC-re-engage micro-puls is a single isolated bucket. Both report
  // mode=revenue_max + engage + battery_w>0 and history carries no planner
  // 'action' field, so run-length is the only signal. Demote charge-runs shorter
  // than MIN_CHARGE_RUN_BUCKETS to 'selfuse' for non-explicit charge-modes;
  // explicit planner charge-modes ('charge'/'pv_fill') always keep 'charge'
  // regardless of duration. This removes phantom 'charge' bands without masking
  // real (multi-hour) grid-charge windows — the regression that reverting the
  // earlier mode-only option B had reintroduced.
  const EXPLICIT_CHARGE_MODES = new Set(['charge', 'pv_fill']);
  const MIN_CHARGE_RUN_BUCKETS = 2; // <2 buckets (<10 min) of ambiguous charge = phantom puls
  for (let i = 0; i <= nowIdx; ) {
    if (pastStrategyByIdx[i] !== 'charge') { i++; continue; }
    let j = i;
    while (j <= nowIdx && pastStrategyByIdx[j] === 'charge') j++;
    if (j - i < MIN_CHARGE_RUN_BUCKETS) {
      for (let k = i; k < j; k++) {
        if (!EXPLICIT_CHARGE_MODES.has(pastModeByIdx[k] ?? '')) {
          pastStrategyByIdx[k] = 'selfuse';
        }
      }
    }
    i = j;
  }

  // BMS-conditionering: een laad-bucket binnen een conditioning-venster krijgt
  // de eigen categorie. Na de phantom-filter, zodat die filter nog op de
  // oorspronkelijke 'charge' draait.
  const condWindows = conditioningWindows(conditioning);
  if (condWindows.length > 0) {
    for (let i = 0; i <= nowIdx; i++) {
      const st = pastStrategyByIdx[i];
      if (st && CHARGE_STRATEGIES.has(st)
          && inWindow(now.getTime() + (i - nowIdx) * STEP_MIN * 60000, condWindows)) {
        pastStrategyByIdx[i] = 'charge_conditioning';
      }
    }
  }

  // SOC forecast trajectory: per-hour action + energy flows + soc trajectory.
  // Uses planner's stored values (pv_kwh, load_kwh, etc.) so battery/grid/load
  // math is self-consistent with what the DP planner committed to.
  // PV-line on chart matches what the planner saw — same as Streamlit dashboard.
  // SLOTLENGTE-BEWUST (2026-07-30, constatering Bram). De planner levert
  // energie PER SLOT, en sinds Vossenjacht op kwartier draait is dat slot geen
  // uur meer. Twee aannames zaten hier ingebakken:
  //
  //   1. elke rij besloeg vier buckets van vijftien minuten;
  //   2. "kWh per uur = kW gemiddeld" — een identiteit die ALLEEN bij uurslots
  //      opgaat.
  //
  // Op kwartierslots gaf dat een factor vier te laag. Bram zag het meteen:
  // "VJ charged niet met 11 maar met 43 kW ... kW is een tijdsonafhankelijke
  // eenheid, kWh is hetgeen dat anders is." Zijn eigen schermafdruk bevatte
  // het bewijs: de weer-PV-lijn (die wél in kW staat) toonde 27,8 kW naast een
  // planner-PV van 7,0 — exact vier keer zo klein.
  //
  // De slotlengte komt uit de reeks zelf, niet uit een aanname: het verschil
  // tussen twee opeenvolgende slotbeginnen. Eén enkel slot, of onleesbare
  // tijden, vallen terug op een uur — het gedrag van vóór deze wijziging.
  // Breedte PER RIJ — de horizon kan gemengd zijn (kwartier vandaag, uur morgen).
  const trajMinuten = slotMinutesPerEntry(socForecast?.trajectory);
  if (socForecast?.trajectory) for (const [ti, t] of socForecast.trajectory.entries()) {
    const slotMin = trajMinuten[ti] ?? 60;
    const trajBuckets = Math.max(1, Math.round(slotMin / STEP_MIN));
    const kwPerKwh = 60 / slotMin;   // kWh per slot -> kW gemiddeld
    const hourIdx = tsToIdx(t.hour_start, now, nowIdx);
    const evKwh = (t.ev_load_kwh ?? 0) * kwPerKwh;
    const evSource = t.ev_overlay_source ?? null;
    for (let k = 0; k < trajBuckets; k++) {
      const i = hourIdx + k;
      if (!inRange(i) || i < nowIdx) continue;
      // SOC: lineair interpoleren tussen begin en eind van het slot.
      if (t.soc_start_pct != null && t.soc_end_pct != null) {
        const frac = k / trajBuckets;
        soF[i] = t.soc_start_pct
          + (t.soc_end_pct - t.soc_start_pct) * (frac + 0.5 / trajBuckets);
      }
      // Vermogens: energie per slot gedeeld door de slotduur.
      if (t.pv_kwh != null) pvF[i] = t.pv_kwh * kwPerKwh;
      if (t.battery_change_kwh != null) baF[i] = t.battery_change_kwh * kwPerKwh;
      if (t.load_kwh != null) glF[i] = t.load_kwh * kwPerKwh;
      if (t.grid_kwh != null) grF[i] = t.grid_kwh * kwPerKwh;
      // EV: extra vermogen toegeschreven aan EV-laden. 0 op slots zonder
      // overlay zodat het niet als reguliere basislast meedoet.
      evF[i] = evKwh;
      // Tariff per hour
      if (buy[i]  == null && t.buy_price  != null) buy[i]  = t.buy_price;
      if (sell[i] == null && t.sell_price != null) sell[i] = t.sell_price;
    }
    // EV-bands: één per slot waarop overlay > 0. CombinedChart kleurt 'm
    // als achtergrond zodat de gebruiker visueel ziet "deze uren is de
    // batterij niet beschikbaar voor verkoop, want EV laadt".
    //
    // SLOTLENGTE-BEWUST (2026-07-30, tweede constatering Bram). Hierboven werd
    // de slotlengte al afgeleid, maar dit blok bleef op vier buckets staan.
    // Op kwartierslots schuift de lus per bucket op terwijl elke band vier
    // buckets breed bleef: vier doorschijnende rechthoeken over elkaar heen,
    // per slot opnieuw. Bram zag het resultaat meteen — "verleden ziet er
    // netjes uit als één transparant blauw blok, maar toekomst zijn allemaal
    // blokjes waardoor je de prognose niet meer kan zien". De randen van die
    // gestapelde rechthoeken vormden het streepjespatroon, en de opgetelde
    // dekking maakte de prognose eronder onleesbaar.
    if (evKwh > 0 && evSource) {
      const startIdx = Math.max(nowIdx, hourIdx);
      const endIdx = Math.min(N_TIMELINE, hourIdx + trajBuckets);
      if (endIdx > startIdx) {
        evBands.push({ startIdx, endIdx, source: evSource });
      }
    }
  }

  // Past EV-bands (2026-05-18 Bram-fix): bouw bands uit echte detector-events
  // (ev_charging_started → ev_charging_ended uit system_events), niet uit
  // ev_active MAX-bucket aggregatie. Reden: bucket-MAX maakt 1 detect-snap
  // een volle 5-min band en kan quasi-EV-windows opleveren bij load-spikes.
  // ev_windows komt direct uit de detector event-log = echte trigger-tijden.
  //
  // Backward-compat: als hist.ev_windows ontbreekt (oude API) → val terug
  // op pastEvActiveByIdx-pad. Verwijderbaar zodra alle Pi's geüpdatet zijn.
  if (hist?.ev_windows && hist.ev_windows.length > 0) {
    for (const w of hist.ev_windows) {
      if (!w.start_ts) continue;
      const startIdx = Math.max(0, tsToIdx(w.start_ts, now, nowIdx));
      const endIdx = w.end_ts
        ? Math.min(nowIdx, tsToIdx(w.end_ts, now, nowIdx))
        : nowIdx;
      if (endIdx > startIdx) {
        evBands.push({ startIdx, endIdx, source: 'detector' });
      }
    }
  } else {
    // Legacy pad — alleen actief tot fleet geüpdatet is.
    let evRunStart = -1;
    for (let i = 0; i <= nowIdx; i++) {
      if (pastEvActiveByIdx[i] && evRunStart === -1) {
        evRunStart = i;
      } else if (!pastEvActiveByIdx[i] && evRunStart !== -1) {
        evBands.push({ startIdx: evRunStart, endIdx: i, source: 'detector' });
        evRunStart = -1;
      }
    }
    if (evRunStart !== -1) {
      evBands.push({ startIdx: evRunStart, endIdx: nowIdx, source: 'detector' });
    }
  }

  // Aaneengesloten banden van dezelfde bron samenvoegen tot één vlak.
  //
  // Het verleden werd al als één doorlopend blok gebouwd (uit de detector-
  // events); de toekomst niet — daar levert de planner één rij per slot. Bij
  // uurslots viel dat niet op, want vier buckets breed en vier buckets opschuiven
  // sluit naadloos aan. Op kwartierslots zijn het er vier keer zoveel, en elke
  // naad tekent zijn eigen rand.
  //
  // Samenvoegen lost dat bij de bron op in plaats van met dekking of randkleur
  // te schuiven: aangrenzende of overlappende banden van dezelfde bron worden
  // één vlak, precies zoals het verleden er al uitziet. Verschillende bronnen
  // blijven gescheiden — die hebben bewust een eigen kleur.
  evBands.sort((a, b) => a.startIdx - b.startIdx || a.endIdx - b.endIdx);
  const samengevoegd: typeof evBands = [];
  for (const band of evBands) {
    const vorige = samengevoegd[samengevoegd.length - 1];
    if (vorige && vorige.source === band.source && band.startIdx <= vorige.endIdx) {
      vorige.endIdx = Math.max(vorige.endIdx, band.endIdx);
    } else {
      samengevoegd.push({ ...band });
    }
  }
  evBands.length = 0;
  evBands.push(...samengevoegd);

  // Latest PV forecast (post-planner): two roles.
  //   1. pvF fallback — fills hours beyond planner horizon so the planner-PV line stays continuous.
  //   2. pvW always — separate live-weather track. Lets users see drift between the snapshot
  //      the planner committed to and the current weather model. Always populated for future buckets.
  if (pvForecast?.hours) for (const h of pvForecast.hours) {
    const hourIdx = tsToIdx(h.hour_start, now, nowIdx);
    for (let k = 0; k < 4; k++) {
      const i = hourIdx + k;
      if (!inRange(i) || i < nowIdx) continue;
      pvW[i] = h.pv_kwh;
      if (pvF[i] == null) pvF[i] = h.pv_kwh;
    }
  }

  // Tarief-terugval: /api/prices vult de buckets die de historie niet dekt.
  //
  // Het aantal buckets komt uit `slot_minutes` en is NIET meer hardgecodeerd
  // op vier. Die vier waren correct zolang elke rij een uur besloeg, maar
  // sinds de prijsvoeding ook kwartierrijen levert zou een kwartierprijs over
  // een heel uur worden uitgesmeerd — vier keer te breed, en precies de
  // verkeerde kant op: de grafiek zou kwartierprijzen tonen als uurblokken en
  // daarmee blijven verbergen waarop de planner stuurde.
  //
  // Ontbrekende `slot_minutes` telt als 60: alle rijen van vóór schema v37
  // zijn uurprijzen.
  if (prices?.prices) for (const p of prices.prices) {
    const startIdx = tsToIdx(p.hour_start, now, nowIdx);
    const slotMin = p.slot_minutes ?? 60;
    const nBuckets = Math.max(1, Math.round(slotMin / STEP_MIN));
    for (let k = 0; k < nBuckets; k++) {
      const i = startIdx + k;
      if (!inRange(i)) continue;
      if (buy[i]  == null) buy[i]  = p.buy_eur_kwh;
      if (sell[i] == null) sell[i] = p.sell_eur_kwh;
    }
  }

  // Connect past↔future at NOW for visual continuity
  for (const [P, F] of [[pvP, pvF], [baP, baF], [grP, grF], [glP, glF], [bkP, bkF], [soP, soF]] as const) {
    if (P[nowIdx] != null && F[nowIdx] == null) F[nowIdx] = P[nowIdx];
  }

  // EV-arrival marker: alleen bij user-hint (niet bij detector). De expected
  // home time komt rechtstreeks uit de hint zodat we ook al voor de
  // detector-overlay kicked in een aankomst-marker tonen.
  let evArrivalIdx: number | null = null;
  let evArrivalSoc: number | null = null;
  let evArrivalLabel: string | null = null;
  const hint = socForecast?.ev_hint;
  if (hint?.expected_home_utc) {
    const idx = tsToIdx(hint.expected_home_utc, now, nowIdx);
    if (inRange(idx) && idx >= nowIdx) {
      evArrivalIdx = idx;
      evArrivalSoc = soF[idx] ?? null;
      const hh = String(hint.expected_home_hour).padStart(2, '0');
      evArrivalLabel = `🔌 EV ${hh}:00 (${hint.empty_pct}% leeg)`;
    }
  }

  // Strategy bands: past from history.mode (per 5-min bucket), future from soc-forecast.
  const bands: TooltipBand[] = [];

  // Past: collapse consecutive same-strategy buckets into single band.
  // Carry first-bucket mode/engagement into the band so the tooltip can show
  // EMS intent (e.g. "revenue_max · yield"). Within a single band these are
  // typically stable — strategy already changes when engagement flips, so
  // taking the first sample's values is safe in practice.
  let runStart = -1;
  let runStrat: StrategyKey | null = null;
  let runMode: string | null = null;
  let runEngagement: string | null = null;
  // Een run knipt ook op begin en einde van een laadpoort-weigering: een
  // geweigerd kwartier wordt gearceerd, het buurkwartier met dezelfde
  // strategie niet. Binnen een geweigerde run knipt niets; de reason komt
  // van het eerste sample van de run.
  let runBlocked: string | null = null;
  let runBlockedReason: string | null = null;
  const pushRun = (endIdx: number) => {
    if (runStart < 0 || !runStrat) return;
    const band: TooltipBand = { startIdx: runStart, endIdx, strategy: runStrat, mode: runMode, engagement: runEngagement };
    if (runBlocked) { band.blockedBy = runBlocked; band.blockedReason = runBlockedReason; }
    bands.push(band);
  };
  for (let i = 0; i <= nowIdx; i++) {
    const s = pastStrategyByIdx[i];
    const blk = s ? pastBlockedByIdx[i] : null;
    // Aaneengesloten geweigerde kwartieren vormen één band, ook als de
    // weigeringscode (marge_eis:zelfgebruik_wachten/_gedekt) of de strategie
    // eronder per kwartier wisselt: anders stapelen de labels "geweigerd"
    // op elkaar (gezien op HO 20-10-2026, 11:00-14:00 NL).
    if (blk && runBlocked) continue;
    if (s === runStrat && blk === runBlocked) continue;
    pushRun(i);
    runStart = s ? i : -1;
    runStrat = s;
    runMode = s ? pastModeByIdx[i] : null;
    runEngagement = s ? pastEngagementByIdx[i] : null;
    runBlocked = blk;
    runBlockedReason = s ? pastBlockedReasonByIdx[i] : null;
  }
  pushRun(nowIdx);

  // Handmatige perioden: aaneengesloten buckets met mode='manual'.
  const manualBands: { startIdx: number; endIdx: number }[] = [];
  for (let i = 0; i <= nowIdx; ) {
    if (pastModeByIdx[i] !== 'manual') { i++; continue; }
    let j = i;
    while (j <= nowIdx && pastModeByIdx[j] === 'manual') j++;
    manualBands.push({ startIdx: i, endIdx: Math.min(j, nowIdx) });
    i = j;
  }

  // Future: from soc-forecast trajectory
  if (socForecast?.trajectory) for (const [ti, t] of socForecast.trajectory.entries()) {
    const startIdx = Math.max(0, tsToIdx(t.hour_start, now, nowIdx));
    // Breedte uit de slotlengte VAN DEZE RIJ — de horizon kan gemengd zijn.
    const buckets = Math.max(1, Math.round((trajMinuten[ti] ?? 60) / STEP_MIN));
    const endIdx = Math.min(N_TIMELINE, startIdx + buckets);
    if (startIdx >= endIdx || endIdx <= nowIdx) continue;
    let strategy: StrategyKey =
      t.action === 'charge' ? 'charge'
      : t.action === 'discharge' ? 'discharge'
      // pv_export is de vierde planner-actie (sinds 2026-07-29). Zonder deze
      // tak viel hij in idleStrategy() en kreeg de VERWACHTING een andere
      // kleur dan dezelfde beslissing een uur later in de historie — precies
      // het soort verschil waardoor de grafiek niet meer uitlegt wat de
      // planner van plan is.
      : t.action === 'pv_export' ? 'feedin'
      : idleStrategy(t.battery_change_kwh);
    // Slot (midden) binnen een conditioning-venster en laadstrategie -> conditionering.
    if (condWindows.length > 0 && CHARGE_STRATEGIES.has(strategy)) {
      const midMs = new Date(t.hour_start).getTime() + ((trajMinuten[ti] ?? 60) / 2) * 60000;
      if (inWindow(midMs, condWindows)) strategy = 'charge_conditioning';
    }
    const bandStart = Math.max(startIdx, nowIdx);
    // Laadpoort-voorspelling: een gepland laadslot dat de poort volgens het
    // huidige plan zal weigeren. Aaneengesloten verwacht-geweigerde slots
    // vormen één band, ook als code of strategie eronder wisselt (zelfde
    // regel als de gemeten weigeringen in het verleden).
    const pred = t.action === 'charge' ? (t.gate_blocked_by_pred ?? null) : null;
    if (pred) {
      const prev = bands[bands.length - 1];
      if (prev && prev.predictedBlockedBy && prev.endIdx === bandStart) {
        prev.endIdx = endIdx;
        continue;
      }
      bands.push({
        startIdx: bandStart, endIdx, strategy,
        predictedBlockedBy: pred, predictedBlockedReason: t.gate_reason_pred ?? null,
      });
      continue;
    }
    bands.push({ startIdx: bandStart, endIdx, strategy });
  }

  return {
    pvP, pvF, pvW,
    baP, baF, grP, grF, glP, glF, bkP, bkF, soP, soF,
    evF, evBands, evArrivalIdx, evArrivalSoc, evArrivalLabel,
    buy, sell, bands, manualBands,
  };
}
